import { forwardRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect } from "react";
import {
  FormInputs,
  formatServicePriceLabel,
  getAddressFromCoordinates,
  schema,
} from "./constants";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { LoadingButton } from "@mui/lab";
import { useRecoilState } from "recoil";
import { notificationsState } from "src/store/notifications";
import { authState } from "src/store/auth";
import sarIcon from "src/assets/svg-icons/sar.svg";
import { useNavigate, useParams } from "react-router-dom";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useGetLocationsList } from "src/api/locations";
import { Service } from "src/types/locations";
import {
  useAddGolfCartRequestMutation,
  useGetGolfCartRequestById,
} from "src/api/golf-carts";
import LocationPickerDialog from "./map-point-picker";

export interface LocationValue {
  lat: number;
  lng: number;
  address?: string;
}

const GolfCartServiceValue = forwardRef<HTMLSpanElement, any>(
  ({ value, className, style, id }, ref) => (
    <span
      ref={ref}
      id={id}
      className={className}
      style={{
        ...style,
        display: "inline-flex",
        alignItems: "center",
        width: "auto",
        gap: 4,
      }}
    >
      {value}
      {value ? <img height={14} src={sarIcon} alt="" /> : null}
    </span>
  ),
);

const AddEditGolfRequestPage = () => {
  const { id } = useParams();
  const mode: "add" | "view" = !id ? "add" : "view";
  const navigate = useNavigate();
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [auth] = useRecoilState(authState);
  const canGiveForFree = auth.permissions?.golf_carts_requests?.giveForFree;
  const [pickupPoint, setPickupPoint] = useState<LocationValue>();
  const [dropOffPoint, setDropOffPoint] = useState<LocationValue>();
  const [activeField, setActiveField] = useState<"pickup" | "dropoff" | null>(
    null,
  );
  const [openLocationDialog, setOpenLocationDialog] = useState(false);

  const handleOpenPickup = () => {
    setActiveField("pickup");
    setOpenLocationDialog(true);
  };

  const handleOpenDropoff = () => {
    setActiveField("dropoff");
    setOpenLocationDialog(true);
  };

  const breadcrumbs = [
    <Link
      key="1"
      color={"#A1A5B7"}
      underline="hover"
      onClick={() => navigate(-1)}
      component="text"
      sx={{ cursor: "pointer" }}
    >
      Golf Cart Requests
    </Link>,

    <Typography key="2" color="#A1A5B7">
      {mode === "add" ? "Add" : "View Request"}
    </Typography>,
  ];

  const methods = useForm<FormInputs>({
    mode: "onBlur",
    resolver: yupResolver(schema),
    defaultValues: {
      isFreeTicket: false,
    },
  });

  const {
    handleSubmit,
    formState: { errors },
    reset,
    control,
    getValues,
  } = methods;

  // APIS
  const { data: locationsData, isFetching: isFetchingLocations } =
    useGetLocationsList({ isClient: true, hasGolfCar: true });
  const { mutate, status, isPending, error } = useAddGolfCartRequestMutation();
  const { data, isFetching } = useGetGolfCartRequestById({
    id: id ?? "",
  });

  const requestData = data?.data;

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    if (!pickupPoint) {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: "Select Pickup Point",
        },
      ]);
      return;
    }

    const { passengers, isFreeTicket, location, serviceName } = data;
    const dataToSend = {
      locationId: location?.id,
      pickupPoint: {
        lat: pickupPoint?.lat,
        lng: pickupPoint?.lng,
      },
      dropOffPoint: dropOffPoint
        ? {
            lat: dropOffPoint?.lat,
            lng: dropOffPoint?.lng,
          }
        : null,
      passengers: +passengers,
      isFreeTicket: isFreeTicket,
      serviceName,
    };
    console.log({ data, dataToSend });
    if (mode === "add") {
      mutate(dataToSend);
    }
  };

  //Reset form with driver data to edit
  useEffect(() => {
    const loadAddress = async () => {
      if (!requestData) return;

      reset({
        location: {
          id: requestData.locationId,
          nameEn: requestData.locationName,
        },
        passengers: requestData.passengers,
        isFreeTicket: requestData.isFreeTicket,
        serviceName: requestData.serviceDisplayName,
      });

      const pickupAddress = requestData?.pickupPoint
        ? await getAddressFromCoordinates(
            requestData?.pickupPoint?.lat as number,
            requestData?.pickupPoint?.lng as number,
            "en",
          )
        : "";

      const dropOffAddress = requestData?.dropOffPoint
        ? await getAddressFromCoordinates(
            requestData?.dropOffPoint?.lat as number,
            requestData?.dropOffPoint?.lng as number,
            "en",
          )
        : "";

      setPickupPoint({
        lat: requestData?.pickupPoint?.lat,
        lng: requestData?.pickupPoint?.lng,
        address: pickupAddress,
      });

      setDropOffPoint(
        requestData?.dropOffPoint
          ? {
              lat: requestData?.dropOffPoint?.lat,
              lng: requestData?.dropOffPoint?.lng,
              address: dropOffAddress,
            }
          : undefined,
      );

      console.log(pickupAddress);
    };

    loadAddress();
  }, [requestData, reset]);

  //Add driver success and error handling
  useEffect(() => {
    if (status === "success") {
      setNotifications([
        ...notifications,
        {
          type: "success",
          message: "Your request has been sent successfully!",
        },
      ]);
      navigate("/golf-requests");
    } else if (status === "error") {
      setNotifications([
        ...notifications,
        {
          type: "error",
          message: error?.data?.message || "Oops! Something went wrong.",
        },
      ]);
    }
  }, [status]);

  if (isFetching || isFetchingLocations) {
    return (
      <Box
        height={"80vh"}
        display={"flex"}
        justifyContent={"center"}
        alignItems={"center"}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={{ xs: "15px", md: "30px" }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormProvider {...methods}>
          <Typography fontSize={"22px"} mb={1} fontWeight={600}>
            {mode === "add" ? "Add Golf Cart Request" : "Edit Request"}
          </Typography>
          <Breadcrumbs
            separator={<NavigateNextIcon fontSize="small" />}
            aria-label="breadcrumb"
            sx={{ color: "#5E6278" }}
          >
            {breadcrumbs}
          </Breadcrumbs>
          <Box
            mt={3}
            p={{ xs: 2, md: 4 }}
            border={"1px solid #5B5B5B"}
            borderRadius={"10px"}
          >
            <Typography mb={3}>Request Details</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                {/* select location */}
                <FormControl fullWidth size="small" error={!!errors.location}>
                  <Controller
                    name="location"
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        fullWidth
                        size="small"
                        {...field}
                        options={locationsData?.locations ?? []}
                        getOptionLabel={(option) => option?.nameEn || ""}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            error={!!errors.location}
                            helperText={errors.location?.message}
                            label={"Location"}
                          />
                        )}
                        renderOption={(props, option, { selected }) => (
                          <li {...props} key={props.id}>
                            <Typography>{option.nameEn}</Typography>
                          </li>
                        )}
                        onChange={(_, data) => {
                          console.log({ data });
                          field.onChange(data);
                          methods.setValue("serviceName", "", {
                            shouldValidate: true,
                          });
                        }}
                        disabled={mode === "view"}
                      />
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                {mode === "add" ? (
                  <Controller
                    name="serviceName"
                    control={control}
                    render={({ field }) => {
                      const selectedLocation = methods.watch("location");
                      const golfCartServices =
                        locationsData?.locations
                          ?.find(
                            (location: any) =>
                              location.id === selectedLocation?.id,
                          )
                          ?.services?.filter(
                            (service: Service) => service.type === "golfCar",
                          ) ?? [];

                      return (
                        <FormControl
                          fullWidth
                          size="small"
                          error={!!errors.serviceName && !!selectedLocation}
                        >
                          <InputLabel id="golf-cart-service-label">
                            Golf Cart Service
                          </InputLabel>
                          <Select
                            {...field}
                            labelId="golf-cart-service-label"
                            id="golf-cart-service"
                            label="Golf Cart Service"
                            value={field.value ?? ""}
                            disabled={!selectedLocation}
                          >
                            {golfCartServices.map((service: Service) => {
                              const price = formatServicePriceLabel(service);

                              return (
                                <MenuItem
                                  key={service.name}
                                  value={service.name}
                                >
                                  {service.name} -{" "}
                                  {price ?? "N/A"}
                                  {price && price !== "free" ? (
                                    <>
                                      {" "}
                                      <img
                                        height={14}
                                        src={sarIcon}
                                        alt=""
                                        style={{ marginLeft: 4 }}
                                      />
                                    </>
                                  ) : null}
                                </MenuItem>
                              );
                            })}
                          </Select>
                          <FormHelperText>
                            {!selectedLocation
                              ? "Select a location first"
                              : errors.serviceName?.message ??
                                (golfCartServices.length === 0
                                  ? "No golf cart services available"
                                  : "")}
                          </FormHelperText>
                        </FormControl>
                      );
                    }}
                  />
                ) : (
                  <Controller
                    name="serviceName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Golf Cart Service"
                        size="small"
                        fullWidth
                        InputProps={{
                          readOnly: true,
                          inputComponent: GolfCartServiceValue,
                        }}
                      />
                    )}
                  />
                )}
              </Grid>

              <Grid item xs={12} md={12}>
                <Stack spacing={2} direction={"row"}>
                  <TextField
                    label="Pickup Point"
                    value={pickupPoint?.address ?? ""}
                    onClick={handleOpenPickup}
                    InputProps={{
                      readOnly: true,
                    }}
                    fullWidth
                    size="small"
                  />

                  <TextField
                    label="Drop-off Point"
                    value={dropOffPoint?.address ?? ""}
                    onClick={handleOpenDropoff}
                    InputProps={{
                      readOnly: true,
                    }}
                    fullWidth
                    size="small"
                    disabled={mode === "view" && !dropOffPoint}
                  />
                </Stack>
              </Grid>

              <Grid item xs={12} md={12}>
                <Controller
                  name="passengers"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      type="number"
                      sx={{ maxWidth: "300px" }}
                      label="NO. of Passengers"
                      {...field}
                      error={!!errors.passengers}
                      helperText={errors.passengers?.message}
                      fullWidth
                      size="small"
                      InputLabelProps={{
                        shrink: field.value !== undefined,
                      }}
                      disabled={mode === "view"}
                    />
                  )}
                />
              </Grid>

              {canGiveForFree && (
                <Grid item xs={12} md={12}>
                  <Controller
                    name="isFreeTicket"
                    control={control}
                    render={({ field }) => (
                      <FormControl
                        error={!!errors.isFreeTicket}
                        disabled={mode === "view"}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={!!field.value}
                              onChange={(e) => field.onChange(e.target.checked)}
                            />
                          }
                          label="Give For Free"
                        />
                        <FormHelperText>
                          {errors.isFreeTicket?.message}
                        </FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
              )}
            </Grid>
          </Box>

          {mode === "add" ? (
            <Stack
              direction={"row"}
              justifyContent={"flex-end"}
              mt={"50px"}
              gap={3}
            >
              <Button
                variant="outlined"
                sx={{ minWidth: { xs: "100px", md: "208px" } }}
                onClick={() => navigate("/golf-requests")}
              >
                Cancel
              </Button>
              <LoadingButton
                type="submit"
                variant="contained"
                sx={{ minWidth: { xs: "100px", md: "208px" } }}
                loading={isPending}
              >
                Send Request
              </LoadingButton>
            </Stack>
          ) : null}
        </FormProvider>
      </form>

      <LocationPickerDialog
        mode={mode}
        open={openLocationDialog}
        onClose={() => {
          setOpenLocationDialog(false);
          setActiveField(null);
        }}
        onConfirm={(location) => {
          if (activeField === "pickup") {
            setPickupPoint(location);
          }

          if (activeField === "dropoff") {
            setDropOffPoint(location);
          }

          setOpenLocationDialog(false);
          setActiveField(null);
        }}
        initialLocation={
          activeField === "pickup"
            ? pickupPoint
              ? {
                  lat: pickupPoint.lat,
                  lng: pickupPoint.lng,
                }
              : undefined
            : dropOffPoint
              ? {
                  lat: dropOffPoint.lat,
                  lng: dropOffPoint.lng,
                }
              : undefined
        }
      />
    </Box>
  );
};

export default AddEditGolfRequestPage;
